import { ReactElement, useMemo } from 'react';
import { addBetSlipEvent } from 'utils/betslip/betslip';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { createArray } from 'utils/helpers/create-fake-array';
import { BetSlipEventObj } from 'interfaces/bet-data';
import { GameInfo, Market } from 'interfaces/sportsbook-data-levels';
import { Odds } from 'components/Odds';
import { useOddChange } from 'hooks/useOddChange';

type TEventsRowProps = {
  market: Market;
  selectedEventIds: string[];
  gameInfo: {
    gameId: number;
    team1_name: string;
    team2_name: string;
    start_ts: number;
    sportAlias: string;
    sportName: string;
    competitionName: string;
    regionName: string;
    info: GameInfo;
    sportId?: number;
  };
  isBlocked: number;
  isLive: boolean;
  oddFormat: number;
  eventsCount?: number;
};

export const ADVANCED_EVENTS_DEFAULT_COUNT = 3;

export const EventsRow = ({
  market,
  selectedEventIds,
  gameInfo,
  isBlocked,
  isLive,
  eventsCount,
  oddFormat
}: TEventsRowProps): ReactElement => {
  const addToBetSlip = (eventId: string | number) => {
    const event = market?.event?.[eventId];

    addBetSlipEvent(
      {
        sport_id: gameInfo.sportId,
        marketType: market?.type || '',
        market: market,
        gameId: gameInfo.gameId,
        eventId: Number(eventId),
        eventBase: event?.base,
        team1: gameInfo.team1_name,
        team2: gameInfo.team2_name || '',
        coeficient: event?.price,
        event_name: event?.name || '',
        game_start_date: gameInfo.start_ts,
        sport_index: gameInfo.sportAlias,
        sport_name: gameInfo.sportName,
        competition_name: gameInfo.competitionName,
        current_game_state: gameInfo.info.current_game_state,
        current_game_time: gameInfo.info.current_game_time,
        isLive,
        region_name: gameInfo.regionName,
        market_name: market?.name || '',
        game_name: `${gameInfo.team1_name} ${
          gameInfo.team2_name ? ` - ${gameInfo.team2_name}` : ''
        }`,
        game_info: gameInfo.info,
        express_id: market?.prematch_express_id || market?.express_id
      } as BetSlipEventObj,
      true
    );
  };

  const { oddChangeHandler, popover, hidePopover } = useOddChange(
    () => {},
    addToBetSlip,
    selectedEventIds
  );

  const events = useMemo(() => {
    const marketEvents = sortByOrder(Object.values(market?.event || {}));

    if (!marketEvents.length) {
      return createArray(eventsCount ?? ADVANCED_EVENTS_DEFAULT_COUNT).map(
        item => ({
          id: item,
          name: '',
          coeficient: Infinity,
          type: '',
          type_1: '',
          is_blocked: 0,
          value: String(item)
        })
      );
    }

    return marketEvents.map(event => ({
      value: String(event.id),
      coeficient: event.price,
      order: event.order,
      name: event.name,
      base: event.base ?? false,
      type1: event.type_1 ? event.type_1 : false,
      marketType: market.market_type
    }));
  }, [market]);

  return (
    <div className="advancedMatchCard__eventsRow">
      <Odds
        showHandicaps
        hideHandicapName
        marketType={market.market_type || market.type}
        value={selectedEventIds}
        gameId={gameInfo.gameId}
        isBlocked={isBlocked}
        onChange={(
          eventId: string,
          price: string,
          sp?: boolean | undefined,
          prevSp?: boolean | undefined
        ) => oddChangeHandler(eventId, Number(price), sp, prevSp)}
        isLive={isLive}
        options={events}
        popover={popover}
        hidePopover={hidePopover}
        oddFormat={oddFormat}
      />
    </div>
  );
};
